import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Stack, Chip, Alert,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Checkbox,
  CircularProgress, Tooltip, Divider, Autocomplete,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import { useSnackbar } from 'notistack';
import api from '../../services/api';

interface PreviewRow {
  id: string;
  orderId: string | null;
  fileName: string | null;
  labName: string | null;
  status: string;
  paramCount: number;
  hasNurseEdits: boolean;
  eligible: boolean;
  reason: string | null;
}

interface SkippedItem {
  id: string;
  reason: string;
}

const BulkReprocess: React.FC<{ embedded?: boolean }> = ({ embedded }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [labName, setLabName] = useState('Redcliffe');
  const [labOptions, setLabOptions] = useState<string[]>([]);
  const [rows, setRows] = useState<PreviewRow[] | null>(null);
  const [excludedByStatus, setExcludedByStatus] = useState<Record<string, number>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  // Known lab names for the dropdown (free text still allowed via freeSolo).
  useEffect(() => {
    let cancelled = false;
    api.get('/reports/bulk-reprocess/labs')
      .then((res) => { if (!cancelled) setLabOptions(res.data?.data || []); })
      .catch(() => { /* dropdown degrades to plain free text */ });
    return () => { cancelled = true; };
  }, []);

  const runPreview = useCallback(async () => {
    setLoading(true); setResult(null); setRows(null); setSelected(new Set()); setExcludedByStatus({});
    try {
      const res = await api.post('/reports/bulk-reprocess/preview', {
        labName: labName.trim() || undefined,
      });
      const data: PreviewRow[] = res.data?.data || [];
      setRows(data);
      setExcludedByStatus(res.data?.excludedByStatus || {});
      // Pre-select the safe ones: eligible, and with no nurse edits to lose.
      setSelected(new Set(data.filter((r) => r.eligible && !r.hasNurseEdits).map((r) => r.id)));
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Preview failed', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [labName, enqueueSnackbar]);

  const toggle = (id: string) => {
    setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const selectableIds = useMemo(() => (rows || []).filter((r) => r.eligible).map((r) => r.id), [rows]);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectableIds));

  const selectedWithEdits = useMemo(
    () => (rows || []).filter((r) => r.hasNurseEdits && selected.has(r.id)).length,
    [rows, selected],
  );

  const selectedApproved = useMemo(
    () => (rows || []).filter((r) => r.status === 'approved' && selected.has(r.id)).length,
    [rows, selected],
  );

  const execute = useCallback(async () => {
    if (selected.size === 0) return;
    const editWarning = selectedWithEdits > 0
      ? `\n\n${selectedWithEdits} of them have nurse edits — those edits will be lost.`
      : '';
    const approvedWarning = selectedApproved > 0
      ? `\n\n${selectedApproved} of them are APPROVED — the approval is reset and they go back to review.`
      : '';
    if (!window.confirm(
      `Reprocess ${selected.size} report(s)? Extraction is redone from scratch.${editWarning}${approvedWarning}`
    )) return;
    setRunning(true);
    try {
      const res = await api.post('/reports/bulk-reprocess/execute', { reportIds: Array.from(selected) });
      const skipped: SkippedItem[] = res.data?.skipped || [];
      setResult(
        `Accepted ${res.data?.accepted ?? 0}; skipped ${skipped.length}. `
        + 'Processing runs in the background — watch the Reports list.'
      );
      setRows(null);
      setSelected(new Set());
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Execute failed', { variant: 'error' });
    } finally {
      setRunning(false);
    }
  }, [selected, selectedWithEdits, selectedApproved, enqueueSnackbar]);

  const busy = loading || running;

  return (
    <Box sx={{ p: embedded ? 0 : 3, maxWidth: 1200, mx: 'auto' }}>
      {!embedded && <Typography variant="h5" fontWeight={700} gutterBottom>Bulk Reprocess</Typography>}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Re-runs extraction on stuck or badly-extracted reports (e.g. after a pipeline fix). Published
        reports are excluded and can never be touched from here — they are corrected via Bulk Re-send.
        Approved (but unpublished) reports CAN be reprocessed: their approval is reset and they return
        to review. Preview first: <b>nothing changes until you press Reprocess</b>.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Autocomplete
            freeSolo size="small" sx={{ minWidth: 280 }}
            options={labOptions}
            value={labName}
            onInputChange={(_, v) => setLabName(v)}
            renderInput={(params) => (
              <TextField
                {...params} label="Lab name filter"
                helperText="Pick a lab or type free text. Partial match. Leave blank for all labs."
              />
            )}
          />
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            onClick={runPreview} disabled={busy}
          >
            Preview
          </Button>
        </Stack>
      </Paper>

      {result && <Alert severity="success" sx={{ mb: 2 }}>{result}</Alert>}

      {rows && rows.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Chip label={`${rows.length} matched`} />
          {/* The preview endpoint caps at 500 rows — say so rather than imply the list is complete. */}
          {rows.length >= 500 && (
            <Chip color="info" variant="outlined" label="500-row cap — narrow the lab filter to see the rest" />
          )}
          {selectedWithEdits > 0 && (
            <Chip color="warning" variant="outlined" label={`${selectedWithEdits} selected with nurse edits`} />
          )}
          {selectedApproved > 0 && (
            <Chip color="warning" variant="outlined" label={`${selectedApproved} approved selected — approval will be reset`} />
          )}
          <Box flexGrow={1} />
          <Chip color="primary" label={`${selected.size} selected`} />
        </Stack>
      )}

      {rows && rows.length === 0 && (
        <Alert severity="info">
          No reprocessable reports match this lab filter.
          {Object.keys(excludedByStatus).length > 0 && (
            <>
              {' '}However, this filter did match{' '}
              {Object.entries(excludedByStatus)
                .map(([status, n]) => `${n} ${status}`)
                .join(', ')}{' '}
              report(s) — those statuses can't be reprocessed from here.
              Published reports are corrected via <b>Bulk Re-send</b> instead.
            </>
          )}
        </Alert>
      )}

      {rows && rows.length > 0 && (
        <Paper variant="outlined">
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small" checked={allSelected}
                      indeterminate={!allSelected && selected.size > 0}
                      onChange={toggleAll} disabled={selectableIds.length === 0}
                    />
                  </TableCell>
                  <TableCell>Order ID</TableCell>
                  <TableCell>File</TableCell>
                  <TableCell>Lab</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Params</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover selected={selected.has(r.id)}>
                    <TableCell padding="checkbox">
                      <Tooltip title={r.eligible ? '' : (r.reason || 'Cannot reprocess')}>
                        <span>
                          <Checkbox
                            size="small" checked={selected.has(r.id)}
                            disabled={!r.eligible} onChange={() => toggle(r.id)}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell><code>{r.orderId || '—'}</code></TableCell>
                    <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={r.fileName || ''}><span>{r.fileName || '—'}</span></Tooltip>
                    </TableCell>
                    <TableCell>{r.labName || '—'}</TableCell>
                    <TableCell><Chip size="small" variant="outlined" label={r.status} /></TableCell>
                    <TableCell align="right">{r.paramCount}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {!r.eligible && <Chip size="small" variant="outlined" label={r.reason || 'Not reprocessable'} />}
                        {r.hasNurseEdits && (
                          <Chip size="small" color="warning" label="has nurse edits — reprocess discards them" />
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider />
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {selected.size} report(s) will be re-extracted from scratch.
            </Typography>
            <Box flexGrow={1} />
            <Button
              variant="contained" color="warning"
              startIcon={running ? <CircularProgress size={16} color="inherit" /> : <ReplayIcon />}
              disabled={busy || selected.size === 0} onClick={execute}
            >
              Reprocess {selected.size > 0 ? `(${selected.size})` : ''}
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default BulkReprocess;
